import { useState } from 'react';
import type {
  IAppUserSourceRef,
  IEntraSourceRef,
  IGithubSourceRef,
  IPersonAccountLink,
  IPersonDetail,
  ISlackSourceRef,
  PersonLinkSource,
} from '@skystone/shared';
import { Dialog } from '@/components/ui/Dialog';
import { Badge } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { KindBadge } from './KindBadge';
import { ChangeKindDialog } from './ChangeKindDialog';
import { MergeWizard } from './MergeWizard';
import { UnlinkConfirmDialog } from './UnlinkConfirmDialog';

interface SourceMaps {
  appUser: Map<string, IAppUserSourceRef>;
  entra: Map<string, IEntraSourceRef>;
  slack: Map<string, ISlackSourceRef>;
  github: Map<string, IGithubSourceRef>;
}

// Render order in the linked-accounts table: directory truth (entra) first,
// app-login next, externals last. Stable across renders so the dialog doesn't
// reshuffle when a link is added.
const SOURCE_ORDER: Record<PersonLinkSource, number> = {
  entra: 0,
  app_user: 1,
  slack: 2,
  github: 3,
};

function IdentityCell({
  link,
  maps,
}: Readonly<{ link: IPersonAccountLink; maps: SourceMaps }>): JSX.Element {
  // Entra is the directory; render a richer two-line block with department +
  // jobTitle on the secondary line. Other sources stick to the single-line
  // identity + muted sourceAccountId layout.
  if (link.source === 'entra') {
    const ref = maps.entra.get(link.sourceAccountId);
    if (ref === undefined) {
      return (
        <>
          <div className="font-medium">{link.sourceAccountId}</div>
          <div className="text-xs text-muted-foreground">{link.sourceAccountId}</div>
        </>
      );
    }
    // Filter null AND empty strings so a partial Entra profile never produces
    // a dangling " · " or a blank primary line. Mirrors PublicLinkedAccountsCard.
    const isNonEmpty = (s: string | null): s is string => s !== null && s.length > 0;
    const primaryParts = [ref.displayName, ref.mail].filter(isNonEmpty);
    const subtitleParts = [ref.department, ref.jobTitle].filter(isNonEmpty);
    const primary = primaryParts.length > 0 ? primaryParts.join(' · ') : link.sourceAccountId;
    return (
      <>
        <div className="font-medium">{primary}</div>
        {subtitleParts.length > 0 && (
          <div className="text-xs text-muted-foreground">{subtitleParts.join(' · ')}</div>
        )}
      </>
    );
  }

  const identity = (() => {
    switch (link.source) {
      case 'app_user':
        return maps.appUser.get(link.sourceAccountId)?.username ?? link.sourceAccountId;
      case 'slack':
        return maps.slack.get(link.sourceAccountId)?.displayName ?? link.sourceAccountId;
      case 'github':
        return maps.github.get(link.sourceAccountId)?.login ?? link.sourceAccountId;
      default:
        return link.sourceAccountId;
    }
  })();
  return (
    <>
      <div className="font-medium">{identity}</div>
      <div className="text-xs text-muted-foreground">{link.sourceAccountId}</div>
    </>
  );
}

interface PersonDetailDialogProps {
  detail: IPersonDetail | null;
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
  onMerged?: (winnerId: string) => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

export function PersonDetailDialog({
  detail,
  open,
  onClose,
  onUpdated,
  onMerged,
}: Readonly<PersonDetailDialogProps>) {
  const [editingKind, setEditingKind] = useState(false);
  const [merging, setMerging] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<IPersonAccountLink | null>(null);

  if (!detail) {
    return (
      <Dialog open={open} onClose={onClose} title="Person details">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </Dialog>
    );
  }

  const { person, links, sourceRefs } = detail;
  const maps: SourceMaps = {
    appUser: new Map(sourceRefs.appUser.map((r) => [r.id, r])),
    entra: new Map(sourceRefs.entra.map((r) => [r.upn, r])),
    slack: new Map(sourceRefs.slack.map((r) => [r.slackId, r])),
    github: new Map(sourceRefs.github.map((r) => [r.login, r])),
  };
  const sortedLinks = [...links].sort(
    (a, b) => SOURCE_ORDER[a.source] - SOURCE_ORDER[b.source],
  );
  const isTombstoned = person.mergedIntoPersonId !== null;

  const handleKindUpdated = (): void => {
    setEditingKind(false);
    onUpdated?.();
  };

  const handleMerged = (): void => {
    setMerging(false);
    if (detail) onMerged?.(detail.person.id);
  };

  const handleUnlinked = (): void => {
    setUnlinkTarget(null);
    onUpdated?.();
  };

  return (
    <Dialog open={open} onClose={onClose} title={person.displayName} maxWidth="max-w-2xl">
      <div className="space-y-6">
        <div className="space-y-1 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Kind:</span>
            <KindBadge kind={person.kind} />
            {!isTombstoned && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingKind(true)}
                aria-label="Change kind"
              >
                Edit
              </Button>
            )}
            {isTombstoned && <Badge variant="warning">Tombstoned</Badge>}
            {!person.isActive && !isTombstoned && <Badge variant="secondary">Inactive</Badge>}
          </div>
          {!isTombstoned && (
            <div className="pt-1">
              <Button size="sm" variant="ghost" onClick={() => setMerging(true)}>
                Merge with another Person…
              </Button>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Email:</span>{' '}
            <span className="font-medium">{person.primaryEmail ?? '—'}</span>
          </div>
          <div className="text-xs text-muted-foreground">
            Created {formatDate(person.createdAt)} · Updated {formatDate(person.updatedAt)}
          </div>
        </div>

        <div>
          <h3 className="mb-2 text-sm font-semibold">Linked accounts ({links.length})</h3>
          {links.length === 0 && (
            <p className="text-sm text-muted-foreground">No linked accounts.</p>
          )}
          {links.length > 0 && (
            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-medium">Source</th>
                    <th className="px-3 py-2 font-medium">Identity</th>
                    <th className="px-3 py-2 font-medium">Match</th>
                    <th className="px-3 py-2 font-medium">Matched at</th>
                    <th className="px-3 py-2 font-medium" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {sortedLinks.map((link) => {
                    // Entra rows are managed by the directory sync; manually
                    // unlinking them is a no-op (the next sync would recreate
                    // the link). Hide the Unlink action to avoid confusing
                    // admins. Other sources keep the button as before.
                    const isEntra = link.source === 'entra';
                    return (
                      <tr key={link.id} className="border-t">
                        <td className="px-3 py-2">
                          <Badge variant="secondary">{link.source}</Badge>
                        </td>
                        <td className="px-3 py-2">
                          <IdentityCell link={link} maps={maps} />
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div>{link.matchSource}</div>
                          <div className="text-muted-foreground">{link.confidence}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {formatDate(link.matchedAt)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {!isTombstoned && !isEntra && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setUnlinkTarget(link)}
                            >
                              Unlink
                            </Button>
                          )}
                          {!isTombstoned && isEntra && (
                            <span
                              className="text-xs text-muted-foreground italic"
                              title="This link is maintained by the Entra sync. Remove the user from the Entra export to unlink."
                            >
                              Managed by Entra
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ChangeKindDialog
        open={editingKind}
        personId={editingKind ? person.id : null}
        currentKind={editingKind ? person.kind : null}
        onClose={() => setEditingKind(false)}
        onConfirmed={handleKindUpdated}
      />

      <MergeWizard
        open={merging}
        winner={merging ? person : null}
        onClose={() => setMerging(false)}
        onMerged={handleMerged}
      />

      <UnlinkConfirmDialog
        open={unlinkTarget !== null}
        link={unlinkTarget}
        personDisplayName={person.displayName}
        totalLinkCount={links.length}
        onClose={() => setUnlinkTarget(null)}
        onConfirmed={handleUnlinked}
      />
    </Dialog>
  );
}
